import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { GradientButton } from './GradientButton';
import { t } from '../i18n';
import type { OffreAnnuelle } from '../utils/annualOffer';
import { colors, radius, shadow, spacing, typography } from '../theme/tokens';

interface AnnualOfferCardProps {
  offre: OffreAnnuelle;
  onSubscribe: () => void;
  busy?: boolean;
  testID?: string;
}

/**
 * Offre Pro annuelle, présentée à côté du Pro mensuel.
 *
 * TOUS LES MONTANTS VIENNENT DU SDK, aucun n'est écrit ici. Le prix affiché est celui du package,
 * déjà formaté dans la devise de l'acheteur ; le prix barré est calculé à l'exécution comme douze
 * mensualités (`utils/annualOffer`). Un montant en dur serait faux hors zone euro, et une réduction
 * annoncée sur un prix jamais pratiqué dans la devise de l'acheteur est une fausse réduction.
 *
 * LE PRIX BARRÉ NE S'AFFICHE QU'ACCOMPAGNÉ DE SA MENTION. Barré seul, il se lit comme un ancien
 * tarif — une promotion qui n'a jamais existé. « soit X/mois × 12 » dit ce qu'il est réellement :
 * l'équivalent au tarif mensuel. Les deux vivent donc dans le même bloc conditionnel, et ni l'un ni
 * l'autre ne s'affiche quand le prix mensuel est indisponible : mieux vaut le seul prix annuel
 * qu'une comparaison approximative.
 */
export function AnnualOfferCard({ offre, onSubscribe, busy = false, testID = 'pw-annual' }: AnnualOfferCardProps) {
  return (
    <View style={styles.carte} testID={testID}>
      <View style={styles.enTete}>
        <Text style={styles.nom}>{t('plansExtra.proAnnual.name')}</Text>
        {offre.remise !== null ? (
          <View style={styles.badge} testID={`${testID}-badge`}>
            <Text style={styles.badgeTexte}>{t('plansExtra.proAnnual.badge', { pourcentage: offre.remise })}</Text>
          </View>
        ) : null}
      </View>

      <View style={styles.ligneprix}>
        <Text style={styles.prix} testID={`${testID}-price`}>
          {offre.prixAnnuel}
          <Text style={styles.periode}> {t('plansExtra.proAnnual.period')}</Text>
        </Text>
        {offre.prixBarre ? (
          <Text style={styles.barre} testID={`${testID}-strikethrough`}>
            {offre.prixBarre}
          </Text>
        ) : null}
      </View>

      {offre.prixBarre && offre.prixMensuel ? (
        <Text style={styles.equivalent} testID={`${testID}-equivalent`}>
          {t('plansExtra.proAnnual.equivalent', { monthly: offre.prixMensuel })}
        </Text>
      ) : null}

      <View style={styles.action}>
        <GradientButton
          label={t('plansExtra.proAnnual.cta')}
          onPress={onSubscribe}
          disabled={busy}
          testID={`${testID}-subscribe`}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  carte: {
    padding: spacing.lg,
    gap: spacing.sm,
    backgroundColor: colors.white,
    borderRadius: radius.xl,
    borderWidth: 1.5,
    borderColor: colors.violet,
    ...shadow.card,
  },
  enTete: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  nom: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.grey900 },
  // Métriques de `Chip` : pilule, teinte douce de l'accent violet.
  badge: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radius.pill,
    backgroundColor: colors.limeSoft,
  },
  badgeTexte: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    lineHeight: typography.caption.lineHeight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey900,
  },
  ligneprix: { flexDirection: 'row', alignItems: 'baseline', gap: spacing.sm },
  prix: { fontSize: typography.h1.size, fontWeight: typography.h1.weight, color: colors.grey900 },
  periode: { fontSize: typography.body.size, fontWeight: typography.body.weight, color: colors.grey500 },
  barre: {
    fontSize: typography.body.size,
    color: colors.grey500,
    textDecorationLine: 'line-through',
  },
  // « en petit mais lisible » : `small` et non `caption`, qui est réservé aux étiquettes capitales.
  equivalent: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  action: { marginTop: spacing.xs },
});
